
import React from "react";
import { Helmet } from 'react-helmet-async';
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import { Clock } from "lucide-react";

const BlogPage = () => {
  const blogPosts = [
    {
      id: "scholarship-profile",
      title: "How to Build a Strong Scholarship Profile as an International Applicant",
      description: "Strategic approaches to showcase your unique qualities and stand out in the competitive international scholarship arena.",
      image: "/lovable-uploads/893dcdd2-b961-440e-a6cb-cb7f8ba830ae.png",
      date: "May 10, 2025",
      readTime: "13 min read"
    },
    {
      id: "scholarship-tips",
      title: "Top Things to Consider Before Applying for Scholarships to Study in the USA, UK, or Canada",
      description: "Essential guidance for international students applying for scholarships to study abroad. Learn about eligibility requirements, application timelines, and how to strengthen your application.",
      image: "/lovable-uploads/62db0e0e-4ddf-4fdc-a28c-1f54344264d9.png",
      date: "May 5, 2025",
      readTime: "14 min read"
    },
    {
      id: "scholarship-mistakes",
      title: "Common Mistakes International Students Make When Applying for Scholarships",
      description: "Avoid these common pitfalls to increase your chances of securing that life-changing scholarship opportunity.",
      image: "/lovable-uploads/34dfaa0e-8286-4ea0-a58e-c6b4ad67ce09.png",
      date: "May 5, 2025",
      readTime: "11 min read"
    },
    {
      id: "common-mistakes",
      title: "Common Mistakes International Applicants Make When Seeking Sponsorship",
      description: "Learn about the common mistakes international applicants make when seeking visa sponsorship and how to avoid them.",
      image: "/lovable-uploads/38300a84-9f3d-405b-b1a1-9ae41d0e6f15.png",
      date: "May 2, 2025",
      readTime: "12 min read"
    },
    {
      id: "employer-sponsorship",
      title: "What Employers Look for When Sponsoring a Work Visa",
      description: "Understand the key factors that influence employers' decisions to sponsor foreign workers for visas.",
      image: "/lovable-uploads/280595a6-6bf9-4a18-a381-50f89cbd24ba.png",
      date: "April 30, 2025",
      readTime: "14 min read"
    },
    {
      id: "non-tech-visa-jobs",
      title: "Visa-Sponsored Jobs for Non-Tech Professionals in 2025",
      description: "Explore diverse opportunities for non-technical professionals seeking international employment with visa sponsorship.",
      image: "/lovable-uploads/9c9d1274-143d-4912-9cb6-964cc86d2cc7.png",
      date: "April 29, 2025",
      readTime: "13 min read"
    },
    {
      id: "top-countries-visa-jobs",
      title: "Top 10 Countries Offering Visa-Sponsored Jobs in 2025",
      description: "Discover the best destinations for international professionals seeking work opportunities with visa sponsorship.",
      image: "/lovable-uploads/043878bf-2912-4bc0-a66e-654e594d4237.png",
      date: "April 27, 2025",
      readTime: "15 min read"
    },
    {
      id: "usa-visa-jobs",
      title: "How to Get Jobs with Visa Sponsorship in USA",
      description: "A comprehensive guide to finding employment with visa sponsorship in the United States.",
      image: "/lovable-uploads/690ef2cc-4841-41f9-ae9f-220d075e6f23.png",
      date: "April 15, 2025",
      readTime: "12 min read"
    },
    {
      id: "canada-visa-jobs",
      title: "How to Get Jobs with Visa Sponsorship in Canada",
      description: "Strategies and resources for securing visa-sponsored employment in Canada.",
      image: "/lovable-uploads/4b9e6694-829c-449a-bf3c-83d141316b7c.png",
      date: "April 12, 2025",
      readTime: "10 min read"
    },
    {
      id: "uk-visa-jobs",
      title: "How to Get Jobs with Visa Sponsorship in United Kingdom",
      description: "Expert advice on navigating the UK job market for international professionals seeking visa sponsorship.",
      image: "/lovable-uploads/bde2328d-b33a-4f3d-8d79-f5e10290a819.png",
      date: "April 10, 2025",
      readTime: "11 min read"
    }
  ];

  return (
    <div className="flex min-h-screen flex-col">
      <Helmet>
        <title>Visanow - Blog</title>
      </Helmet>
      <Navbar />
      <main className="flex-1 bg-gray-50">
        <div className="container mx-auto py-16">
          <div className="mb-12 text-center">
            <h1 className="mb-3 text-5xl font-bold tracking-tight text-gray-900">Visa Now Blog</h1>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Expert insights and comprehensive guides for international professionals seeking visa-sponsored employment opportunities worldwide.
            </p>
          </div>
          
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts.map((post) => (
              <Link to={`/blog/${post.id}`} key={post.id} className="transition-transform duration-300 hover:-translate-y-1">
                <Card className="h-full overflow-hidden border-none bg-white shadow-md transition-shadow hover:shadow-xl">
                  <div className="aspect-video w-full overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                  <CardHeader className="space-y-2">
                    <CardTitle className="line-clamp-2 text-2xl font-bold">{post.title}</CardTitle>
                    <CardDescription className="line-clamp-2 text-base text-gray-600">
                      {post.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <time dateTime={post.date} className="font-medium">
                        {post.date}
                      </time>
                      <span className="flex items-center">
                        <Clock className="mr-1 h-4 w-4" />
                        {post.readTime}
                      </span>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      variant="outline" 
                      className="w-full border-gray-200 bg-white/50 font-medium text-gray-900 backdrop-blur hover:bg-gray-50"
                    >
                      Read Article
                    </Button>
                  </CardFooter>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
